import { MedplumClient } from "@medplum/core";
import type { Observation, Reference, Resource } from "@medplum/fhirtypes";

import type { LaborAmendDisplayDetail } from "@/encounters/components/EncounterLabor/types/labor-amend";
import type {
  BirthObservationsLoadStatus,
  LaborStagesLoadStatus,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock-ready";
import type {
  LaborAmendProvenanceSnapshot,
  LaborAmendStageMembershipChangeRecord,
} from "@/encounters/types/labor-amend-provenance";

export type LaborAmendFlowsheetFlushResult =
  | { ok: true; succeededJobCount: number }
  | { ok: false; error: Error; succeededJobCount: number };

export type LaborAmendPendingDisplayEntryType =
  | "quick-add"
  | "stage-move"
  | "flowsheet-edit"
  | "generic";

export interface LaborAmendPendingDisplayEntry {
  key: string;
  title: string;
  entryType?: LaborAmendPendingDisplayEntryType;
  /** LOINC labor-stage answer code (e.g. prodromal / active); used for pending quick-add rows. */
  laborStageLoincCode?: string;
  /** Structured rows shown under the entry title in the pending-changes banner. */
  details?: LaborAmendDisplayDetail[];
  /** Legacy inline summary; parsed into rows when `details` is omitted. */
  subtitle?: string;
}

export interface EnqueueFlowsheetPersistOptions {
  /** When set, replaces any queued job with the same key (matches pending display dedupe). */
  replaceKey?: string;
}

export interface FlowsheetPersistQueueEntry {
  job: () => Promise<void>;
  replaceKey?: string;
}

export interface LaborAmendFlowsheetPersistContextValue {
  /** When true, labor/postpartum flowsheet Medplum writes are queued until Sign & Lock. */
  deferFlowsheetPersists: boolean;
  /** When false, flowsheet queue/preview edits are blocked (e.g. labor stages failed to load). */
  isFlowsheetEditingEnabled: boolean;
  /** Amend-only: used for blocked-edit toasts when prerequisites failed to load. */
  laborStagesLoadStatus?: LaborStagesLoadStatus;
  birthObservationsLoadStatus?: BirthObservationsLoadStatus;
  /** True while a flush or Sign & Lock is in progress; new queue entries are rejected. */
  isFlowsheetPersistLocked: boolean;
  /** True after at least one queued job was written to Medplum during this amend session. */
  hasCommittedFlowsheetToServer: boolean;
  enqueueFlowsheetPersist: (
    job: () => Promise<void>,
    options?: EnqueueFlowsheetPersistOptions,
  ) => boolean;
  flushFlowsheetPersistQueue: () => Promise<LaborAmendFlowsheetFlushResult>;
  /** Flushes until the queue is empty (including jobs added during an in-flight flush). */
  drainFlowsheetPersistQueue: () => Promise<LaborAmendFlowsheetFlushResult>;
  /** Clears pending banner entries and observation preview patches after a full Sign & Lock. */
  commitAmendLocalPreviews: () => void;
  discardFlowsheetPersistQueue: () => void;
  setSignAndLockInProgress: (inProgress: boolean) => void;
  registerPostAmendmentEffect: (effect: () => Promise<void>) => void;
  runPostAmendmentEffects: () => Promise<void>;
  /** Clears the post-amendment effect queue after encounter save succeeds. */
  finalizePostAmendmentEffects: () => void;
  /** Human-readable queued edits (forms that are not yet reflected as Observation patches). */
  pendingDisplayEntries: LaborAmendPendingDisplayEntry[];
  registerPendingDisplayEntry: (entry: LaborAmendPendingDisplayEntry) => void;
  getPendingEntryLaborStageLoincCode: (key: string) => string | undefined;
  /** Shallow-merges onto member or labor-stage Observations when rendering the labor table. */
  setObservationPreviewPatch: (observationId: string, patch: Partial<Observation>) => void;
  getObservationPreviewPatch: (observationId: string) => Partial<Observation> | undefined;
  applyObservationAmendPreview: (observation: Observation) => Observation;
  hasLocalAmendPreviews: boolean;
  /** True when sessionStorage indicates unflushed amend work was lost after a refresh. */
  hasLostPendingWorkAfterRefresh: boolean;
  /** True when sessionStorage indicates post-amendment effects were lost after a refresh. */
  hasLostPostAmendmentEffectsAfterRefresh: boolean;
  /**
   * Returns labor-stage observations during flush. Caches within a single job; invalidated after
   * each successful queued job so later jobs see stages created earlier in the same batch.
   */
  getLaborStageObservationsForFlush: (
    medplum: MedplumClient,
    clientId: string,
    encounterId: string,
    fetchLaborStageObservations: (
      medplum: MedplumClient,
      clientId: string,
      encounterId: string,
    ) => Promise<Observation[]>,
  ) => Promise<Observation[]>;
  /**
   * Encounter QuestionnaireResponse `meta.versionId` to stamp on flowsheet QR writes during this amend.
   * Set to the amended encounter QR version after Sign & Lock saves the encounter form.
   */
  encounterQuestionnaireParentVersionId: string | undefined;
  setEncounterQuestionnaireParentVersionId: (versionId: string | undefined) => void;
  getEncounterQuestionnaireParentVersionIdForStamp: () => string | undefined;
  /** When deferring persists, only true during Sign & Lock flush so live paths cannot write Medplum early. */
  canExecuteDeferredFlowsheetPersist: () => boolean;
  /** Registers a resource changed during amend for amend-completion Provenance targets. */
  registerAmendProvenanceTarget: (resourceOrReference: Resource | Reference<Resource>) => void;
  /** Records a labor-stage membership move for the version diff view. */
  registerAmendStageMembershipChange: (change: LaborAmendStageMembershipChangeRecord) => void;
  /** Returns a copy of the amend-session Provenance snapshot without clearing it. */
  peekAmendProvenanceSnapshot: () => LaborAmendProvenanceSnapshot;
  /** Clears the amend-session Provenance snapshot after persistence succeeds. */
  finalizeAmendProvenanceSnapshot: () => void;
}

export const defaultLaborAmendFlowsheetPersistContextValue: LaborAmendFlowsheetPersistContextValue =
  {
    deferFlowsheetPersists: false,
    isFlowsheetEditingEnabled: true,
    isFlowsheetPersistLocked: false,
    hasCommittedFlowsheetToServer: false,
    enqueueFlowsheetPersist: () => false,
    flushFlowsheetPersistQueue: async () => ({ ok: true, succeededJobCount: 0 }),
    drainFlowsheetPersistQueue: async () => ({ ok: true, succeededJobCount: 0 }),
    commitAmendLocalPreviews: () => {},
    discardFlowsheetPersistQueue: () => {},
    setSignAndLockInProgress: () => {},
    registerPostAmendmentEffect: () => {},
    runPostAmendmentEffects: async () => {},
    finalizePostAmendmentEffects: () => {},
    pendingDisplayEntries: [],
    registerPendingDisplayEntry: () => {},
    getPendingEntryLaborStageLoincCode: () => undefined,
    setObservationPreviewPatch: () => {},
    getObservationPreviewPatch: () => undefined,
    applyObservationAmendPreview: (observation) => observation,
    hasLocalAmendPreviews: false,
    hasLostPendingWorkAfterRefresh: false,
    hasLostPostAmendmentEffectsAfterRefresh: false,
    getLaborStageObservationsForFlush: async (_medplum, _clientId, _encounterId, fetch) =>
      fetch(_medplum, _clientId, _encounterId),
    encounterQuestionnaireParentVersionId: undefined,
    setEncounterQuestionnaireParentVersionId: () => {},
    getEncounterQuestionnaireParentVersionIdForStamp: () => undefined,
    canExecuteDeferredFlowsheetPersist: () => false,
    registerAmendProvenanceTarget: () => {},
    registerAmendStageMembershipChange: () => {},
    peekAmendProvenanceSnapshot: () => ({ targets: [], stageMembershipChanges: [] }),
    finalizeAmendProvenanceSnapshot: () => {},
  };
